import { useEffect, useState } from 'react'
import { TraceGroup } from '../types/panel'

interface Props {
  groups: TraceGroup[]
  isActive: boolean
  /** Live-Reasoning des Agenten (reasoning_delta), '' = keine Zeile */
  reasoningText?: string
  /** Finales Reasoning nach Abschluss — im aufgeklappten Trace als "Gedacht" */
  finalReasoning?: string
  /** Laufzeit des abgeschlossenen Laufs in Sekunden (für die Zusammenfassung) */
  durationSeconds?: number
}

/** Rollierende Vorschau: nur das Ende interessiert, alte Zeichen fallen raus. */
const REASONING_TAIL = 200

/** Gruppen als Liste rendern: Kopfzeile pro Agent, Steps eingerückt darunter. */
function TraceGroups({ groups, done }: { groups: TraceGroup[]; done: boolean }) {
  return (
    <>
      {groups.map(g => {
        const badge = [g.schwierigkeit, g.model].filter(Boolean).join(' · ')
        return (
          <li key={g.agent} className="k-trace__agent">
            <span className="txt">{g.agent}</span>
            {badge && <span className="k-trace__badge">{badge}</span>}
            <span className="k-trace__state">
              {g.status === 'done' || done ? '✓' : <span className="k-cursor"></span>}
            </span>
            {g.steps.length > 0 && (
              <ul className="k-trace__steps">
                {g.steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ul>
            )}
          </li>
        )
      })}
    </>
  )
}

/** Tool-Trace des Agenten im Kontor-Stil (k-trace, Mono), gruppiert pro Agent. */
export default function ThinkingIndicator({
  groups, isActive, reasoningText, finalReasoning, durationSeconds,
}: Props) {
  // Zusammenfassung nach Abschluss: Default eingeklappt, Klick klappt auf/zu
  const [expanded, setExpanded] = useState(false)
  useEffect(() => {
    if (isActive) setExpanded(false)
  }, [isActive])

  // Auch ohne Steps rendern, solange der Agent aktiv ist — sonst entsteht
  // zwischen Absenden und erstem Thinking-Event eine stumme Totzeit.
  if (groups.length === 0 && !isActive) return null

  // Abgeschlossen: Einzeiler-Zusammenfassung, aufklappbar zum vollen Trace.
  // Orchestrator-Kopfgruppe zählt nicht als Agent, sobald Sub-Agenten liefen.
  if (!isActive) {
    const totalSteps = groups.reduce((n, g) => n + g.steps.length, 0)
    const subAgents = groups.filter(g => g.agent !== 'Orchestrator').length
    const agentCount = subAgents > 0 ? subAgents : groups.length
    return (
      <div className="k-msg k-msg--agent">
        <div className="k-msg__who">Kontor</div>
        <button
          type="button"
          className="k-trace__summary"
          onClick={() => setExpanded(e => !e)}
          aria-expanded={expanded}
        >
          ✓ {totalSteps} {totalSteps === 1 ? 'Schritt' : 'Schritte'}
          {' · '}{agentCount} {agentCount === 1 ? 'Agent' : 'Agenten'}
          {' · '}{durationSeconds ?? 0}s
        </button>
        {expanded && (
          <ul className="k-trace k-trace--done">
            {finalReasoning && (
              <li className="k-trace__reason k-trace__reason--done">
                <span className="txt">Gedacht: {finalReasoning}</span>
              </li>
            )}
            <TraceGroups groups={groups} done />
          </ul>
        )}
      </div>
    )
  }

  return (
    <div className="k-msg k-msg--agent">
      <div className="k-msg__who">Kontor</div>
      <ul className="k-trace">
        {reasoningText && (
          <li className="k-trace__reason">
            <span className="txt">Überlegt: …{reasoningText.slice(-REASONING_TAIL)}</span>
          </li>
        )}
        <TraceGroups groups={groups} done={false} />
        {groups.length === 0 && <li>arbeitet<span className="k-cursor"></span></li>}
      </ul>
    </div>
  )
}
